import React, { useEffect, useState } from "react"

const YoutubeComponent = ({ blok: { Youtube } }) => {
	const [videoId, setVideoId] = useState("")
	const [error, setError] = useState(null)
	const [isMounted, setIsMounted] = useState(false)

	// Ensure component only renders iframe on client-side to avoid SSR issues
	useEffect(() => {
		setIsMounted(true)
	}, [])

	useEffect(() => {
		if (!Youtube) {
			setError("No YouTube URL provided")
			return
		}

		let extractedVideoId = ""

		try {
			const youtubeUrl = Youtube.trim()

			// Handle full YouTube watch URLs (with or without https)
			if (youtubeUrl.includes("youtube.com/watch")) {
				try {
					const url = new URL(
						youtubeUrl.startsWith("http") ? youtubeUrl : `https://${youtubeUrl}`
					)
					extractedVideoId = url.searchParams.get("v")
				} catch {
					// Fallback parsing if URL constructor fails
					const match = youtubeUrl.match(/[?&]v=([a-zA-Z0-9_-]{11})/)
					if (match) extractedVideoId = match[1]
				}
			}
			// Handle youtu.be short URLs
			else if (youtubeUrl.includes("youtu.be/")) {
				try {
					const url = new URL(
						youtubeUrl.startsWith("http") ? youtubeUrl : `https://${youtubeUrl}`
					)
					const pathParts = url.pathname.split("/").filter(p => p)
					extractedVideoId = pathParts[pathParts.length - 1]?.split("?")[0]
				} catch {
					// Fallback parsing
					const match = youtubeUrl.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/)
					if (match) extractedVideoId = match[1]
				}
			}
			// Handle embed URLs
			else if (youtubeUrl.includes("youtube.com/embed/")) {
				try {
					const url = new URL(
						youtubeUrl.startsWith("http") ? youtubeUrl : `https://${youtubeUrl}`
					)
					const pathParts = url.pathname.split("/").filter(p => p)
					extractedVideoId = pathParts[pathParts.length - 1]?.split("?")[0]
				} catch {
					const match = youtubeUrl.match(/embed\/([a-zA-Z0-9_-]{11})/)
					if (match) extractedVideoId = match[1]
				}
			}
			// Handle just the video ID (11 characters)
			else if (/^[a-zA-Z0-9_-]{11}$/.test(youtubeUrl)) {
				extractedVideoId = youtubeUrl
			}

			// Clean up video ID (remove any query params or fragments)
			if (extractedVideoId) {
				extractedVideoId = extractedVideoId.split("?")[0].split("#")[0]
			}

			if (extractedVideoId && extractedVideoId.length === 11) {
				setVideoId(extractedVideoId)
				setError(null)
			} else {
				setError(`Invalid YouTube URL format. Extracted: "${extractedVideoId}"`)
				console.error("YouTube URL parsing failed:", {
					original: Youtube,
					extracted: extractedVideoId,
				})
			}
		} catch (err) {
			setError("Error parsing YouTube URL")
			console.error("YouTube URL parsing error:", err, "URL:", Youtube)
		}
	}, [Youtube])

	if (error) {
		return (
			<div className="p-4 bg-red-50 border border-red-200 rounded">
				<p className="text-red-600">Error loading YouTube video: {error}</p>
				{Youtube && <p className="text-sm text-red-500 mt-2">URL: {Youtube}</p>}
			</div>
		)
	}

	if (!videoId) {
		return null
	}

	// Don't render iframe during SSR - wait for client-side mount
	if (!isMounted) {
		return (
			<div className="youtube-wrapper" style={{ minHeight: "400px" }}>
				<div className="flex items-center justify-center h-full">
					<p>Loading video...</p>
				</div>
			</div>
		)
	}

	// Build embed URL - use standard format without extra parameters
	// Error 153 is often caused by origin parameter or domain restrictions
	const embedUrl = `https://www.youtube.com/embed/${videoId}`

	return (
		<div className="youtube-wrapper">
			<iframe
				className="responsive-iframe"
				src={embedUrl}
				title="YouTube video player"
				frameBorder="0"
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
				allowFullScreen
				loading="lazy"
				// Add referrer policy to help with domain restrictions
				referrerPolicy="strict-origin-when-cross-origin"
			/>
			{/* <p className="text-center mt-2 text-sm">
				<a
					href={`https://www.youtube.com/watch?v=${videoId}`}
					target="_blank"
					rel="noopener noreferrer"
					className="text-blue-600 hover:underline"
				>
					Watch video on YouTube
				</a>
			</p> */}
		</div>
	)
}

export default YoutubeComponent
